import { VariantOption } from './typing/shared-types.js'
import {
  Attribute, AttributeValue,
  AvailableProductVariant,
  NewFilterOptions,
  RedirectResponseLegacy,
  RedirectResponseNew,
  VariantOptions,
  VariantOptionsString
} from './typing/product-types.js'
import {
  CheckForNewOptionsRequest,
  FetchAttributesRequest,
  VARIANT_FILTER_API,
  VariantFilterApi
} from './variant-filter-api.js'
import { VariantFilterParser } from './variant-filter-parser.js'
import typia from 'typia'
import { logValidationFailure } from 'components/shared/parsing/parsing.js'
import { FAKE, inject, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { createSignal } from '@cloudsuite/mosaic-shared/scripts/signals.js'

export const VARIANT_FILTER_STORE = Symbol('VARIANT_FILTER_STORE')

const OTHER_COMBINATION_PARAMETER = 'othercombination'

export const isDropdownFilter = (filter: Attribute): boolean =>
  filter.display_mode === undefined || filter.display_mode === 'dropdown'

@registerDependency(REAL, VARIANT_FILTER_STORE)
@registerDependency(FAKE, VARIANT_FILTER_STORE)
export class VariantFilterStore {

  @inject(WINDOW_LOCATION)
  public windowLocation!: WindowLocation

  @inject(VARIANT_FILTER_API)
  private api!: VariantFilterApi

  private pendingRequest: AbortController | null = null

  variantOptions: () => Attribute[]
  setVariantOptions: (options: Attribute[]) => void

  parser: VariantFilterParser = new VariantFilterParser()
  attributesUrl: string = ''

  constructor() {
    [this.variantOptions, this.setVariantOptions] = createSignal<Attribute[]>([])
  }

  optionSelected = async (option: VariantOption) => {
    const usesFallbackFilters = this.updateSelectedFilters(option)
    const request = new CheckForNewOptionsRequest(
      this.attributesUrl, {
        'attribute': option.key,
        'value': option.value.toString(),
        'filters': parsePayloadFilters(this.variantOptions()),
      }
    )

    this.pendingRequest?.abort()
    this.pendingRequest = request.controller

    await this.redirectToOtherVariantOrUpdateSelectedFilters(request, usesFallbackFilters)
  }

  private async redirectToOtherVariantOrUpdateSelectedFilters(request: CheckForNewOptionsRequest, usesFallbackFilters: boolean) {
    let response: unknown
    try {
      response = await this.api.checkForRedirectOrNewFilterOptions(request)
    } catch (error) {
      if (!request.controller.signal.aborted) {
        console.error('Variant filter request failed', error)
      }
      return
    }

    if (request.controller.signal.aborted) {
      return
    }

    const legacyRedirectValidation = typia.validate<RedirectResponseLegacy>(response)
    if (legacyRedirectValidation.success) {
      this.windowLocation.redirect(buildRedirectUrl(legacyRedirectValidation.data.url, usesFallbackFilters))
      return
    }

    const newRedirectValidation = typia.validate<RedirectResponseNew>(response)
    if (newRedirectValidation.success) {
      this.windowLocation.redirect(buildRedirectUrl(newRedirectValidation.data.final_data.url, usesFallbackFilters))
      return
    }

    const newFiltersValidation = typia.validate<NewFilterOptions>(response)
    if (newFiltersValidation.success) {
      this.setVariantOptions(
        this.parser.mergeFilterOptions(this.variantOptions(), newFiltersValidation.data)
      )

      this.updateSelectedFilters({ key: newFiltersValidation.data.attr.toString(), value: 0 })
      return
    }

    logValidationFailure('variant filter response as RedirectResponseLegacy', legacyRedirectValidation.errors)
    logValidationFailure('variant filter response as RedirectResponseNew', newRedirectValidation.errors)
    logValidationFailure('variant filter response as NewFilterOptions', newFiltersValidation.errors)
  }

  private updateSelectedFilters(selectedOption: VariantOption): boolean {
    const currentFilters: Attribute[] = this.variantOptions()
    const changedFilterIndex: number = currentFilters.findIndex(filter => filter.id === parseInt(selectedOption.key))
    const currentChangedFilter: Attribute | undefined = currentFilters[changedFilterIndex]

    if (!currentChangedFilter) {
      console.error('No filter found for id', selectedOption.key)
      return false
    }

    const previousFilters: Attribute[] = currentFilters.slice(0, changedFilterIndex)
    const changedFilter: Attribute = {
      ...currentChangedFilter,
      options: setSelectedOption(currentChangedFilter.options, selectedOption.value),
    }
    const newSelectedOption: AttributeValue | undefined = changedFilter.options.find(option => option.selected)
    const selectedOptionExists: boolean = newSelectedOption !== undefined && newSelectedOption.exists !== false

    const underlyingFilters: Attribute[] = currentFilters.slice(changedFilterIndex + 1)
    const updatedUnderlyingFilters: Attribute[] = selectedOptionExists
      ? underlyingFilters
      : underlyingFilters.map(filter => ({ ...filter, options: deselectAllSelectedOptions(filter.options) }))

    const filters: Attribute[] = [...previousFilters, changedFilter, ...updatedUnderlyingFilters]

    const fallbackProductFilters: VariantOptions | null = newSelectedOption?.exists === false && !underlyingFilters.some(isDropdownFilter)
      ? getFallbackProductFilters(
        [...getSelectedOptions(previousFilters), { key: String(changedFilter.id), value: newSelectedOption.id }],
        changedFilter.available_product_variants
      )
      : null

    if (!fallbackProductFilters) {
      this.setVariantOptions(filters)
      return false
    }

    this.setVariantOptions(filters.map(filter => ({
      ...filter,
      options: setSelectedOption(filter.options, fallbackProductFilters[filter.id]),
    })))
    return true
  }

  fetchAttributes = async (url: string) => {
    const response = await this.api.fetchAttributes(
      new FetchAttributesRequest(url)
    )

    if (!typia.is<Attribute[]>(response)) {
      throw new Error('Invalid data for initial call variant filter')
    }

    this.setVariantOptions(response)
  }

  setAttributesUrl(url: string) {
    this.attributesUrl = url
  }
}

const buildRedirectUrl = (path: string, usesFallbackFilters: boolean): URL => {
  const url = new URL(window.location.origin + path)

  if (usesFallbackFilters) {
    url.search = url.search
      ? `${url.search}&${OTHER_COMBINATION_PARAMETER}`
      : OTHER_COMBINATION_PARAMETER
  }

  return url
}

const parsePayloadFilters = (variantOptions: Attribute[]): VariantOptionsString => {
  const result: VariantOptionsString = {}

  for (const filter of variantOptions) {
    const selected = filter.options.find(option => option.selected)
    if (selected) {
      result[filter.id] = selected.id.toString()
    }
  }

  return result
}

const getSelectedOptions = (filters: Attribute[]): VariantOption[] => {
  return filters.flatMap((filter: Attribute): VariantOption[] => {
    const selected: AttributeValue | undefined = filter.options.find(option => option.selected)
    return selected ? [{ key: String(filter.id), value: selected.id }] : []
  })
}

const getFallbackProductFilters = (
  options: VariantOption[],
  availableProductVariants: AvailableProductVariant[] | null | undefined
): VariantOptions | null => {
  const match: AvailableProductVariant | undefined = (availableProductVariants ?? []).find(
    variant => options.every(option => variant.attributes[option.key] === option.value)
  )

  if (!match) {
    console.error('[fallback] No variant found for option', options.at(-1))
    return null
  }

  return match.attributes
}

const setSelectedOption = (options: AttributeValue[], optionId: number | undefined): AttributeValue[] => {
  return options.map(option => ({
    ...option,
    selected: option.id === optionId,
  }))
}

const deselectAllSelectedOptions = (options: AttributeValue[]): AttributeValue[] => {
  return options.map(option => ({
    ...option,
    selected: false,
  }))
}
